import { useIsRestoring, useQueryClient } from "@tanstack/react-query";
import { deleteToken } from "firebase/messaging";
import { useRouter } from "next/router";
import { useEffect } from "react";
import useNavigatorOnline from "use-navigator-online";
import localforage from "localforage";
import { toast } from "~served/ui-comps/toast";
import { CALENDAR_VIEW, PREFERRED_LOCATION_VIEW } from "~/configs/constants";
import { messaging } from "~/libs/firebase";
import { STORAGE_KEYS } from "~/libs/storage-keys";
import { OfflineMutation } from "~/libs/offline-mutations";
import { useLoginSilent, useLoginSilentOffline } from "~/queries/use-login";
import { setState, useSetIsReady, useSetVenueId, useVenueId } from "~/store";
import { State } from "~/types/store";
import {
	buildLoginUrl,
	decodeRedirectPath,
	getDefaultPostLoginDestination,
} from "~/utils/redirect-helpers";
import { getCurrentTillDestination } from "~/utils/till-route";

export const useInitApp = () => {
	const router = useRouter();
	const isRestoringPersistedData = useIsRestoring();
	const queryClient = useQueryClient();
	const { isOffline } = useNavigatorOnline();

	useRestoreNonSensitiveLocalStorageData();

	const venueId = useVenueId();
	const setIsReady = useSetIsReady();
	const setVenueId = useSetVenueId();
	const loginSilent = useLoginSilent();
	const loginSilentOffline = useLoginSilentOffline();

	// INIT `venueId` state
	useEffect(() => {
		async function start() {
			if (isRestoringPersistedData) return;
			if (venueId) return;

			// use this instead of query, to get id on the first render
			const url = new URL(window.location.href);
			const queryId =
				url.searchParams.get("id") ?? url.pathname.replaceAll(/\/|login/g, "");

			// accessing /login/[id] page on first start
			if (url.pathname.startsWith("/login") && !!queryId) {
				const cachedVenueId = await localforage.getItem<string>(
					STORAGE_KEYS.VENUE_ID,
				);

				// same venue
				if (cachedVenueId === queryId) {
					setVenueId(queryId);
					return;
				}

				// accessing different venue
				queryClient.clear();
				await deleteToken(messaging).catch(console.log);
				await localforage.removeItem(STORAGE_KEYS.TOKEN);
				await localforage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
				await localforage.removeItem(STORAGE_KEYS.LAST_LOGGED_IN_ROLE_ID);
				await localforage.removeItem(STORAGE_KEYS.PREFERRED_LOCATION_ON_TILL);
				await localforage.setItem(STORAGE_KEYS.VENUE_ID, queryId);
				setVenueId(queryId);
				return;
			}

			// NOT accessing /login page on first start
			const cachedVenueId = await localforage.getItem<string>(
				STORAGE_KEYS.VENUE_ID,
			);
			if (!cachedVenueId) {
				// Determine redirect path: use current path or existing redir param
				let redirectTo: string | undefined;
				if (url.pathname !== "/" && !url.pathname.startsWith("/login")) {
					redirectTo = url.pathname + url.search + url.hash;
				} else {
					// Check if there's already a redir param in the URL
					const existingRedir = url.searchParams.get("redir");
					if (existingRedir) {
						redirectTo = decodeURIComponent(existingRedir);
					}
				}

				await router.replace(
					buildLoginUrl({
						venueId: "",
						redirectTo,
					}),
				);
				setIsReady(true);
				return;
			}

			setVenueId(cachedVenueId);
		}

		void start();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isRestoringPersistedData, venueId, router.pathname, router.query.id]);

	// init app process only after `venueId` is set
	useEffect(() => {
		async function start() {
			if (!venueId) return;
			if (isRestoringPersistedData) return;

			// use this instead of query, to get id on the first render
			const url = new URL(window.location.href);
			const queryId =
				url.searchParams.get("id") ?? url.pathname.replaceAll(/\/|login/g, "");

			// accessing /login/[id] page on first start
			if (url.pathname.startsWith("/login") && !!queryId) {
				const cachedVenueId = await localforage.getItem<string>(
					STORAGE_KEYS.VENUE_ID,
				);

				// same venue
				if (cachedVenueId !== queryId) {
					queryClient.clear();
					await deleteToken(messaging).catch(console.log);
					await localforage.removeItem(STORAGE_KEYS.TOKEN);
					await localforage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
					await localforage.removeItem(STORAGE_KEYS.LAST_LOGGED_IN_ROLE_ID);
					await localforage.removeItem(STORAGE_KEYS.PREFERRED_LOCATION_ON_TILL);
					await localforage.setItem(STORAGE_KEYS.VENUE_ID, queryId);
					setVenueId(queryId);
					return;
				}
			}

			// found login token
			try {
				const cachedRefreshToken = await localforage.getItem<string>(
					STORAGE_KEYS.REFRESH_TOKEN,
				);
				const roleId = await localforage.getItem<string>(
					STORAGE_KEYS.LAST_LOGGED_IN_ROLE_ID,
				);

				if (isOffline) {
					if (roleId) {
						await loginSilentOffline.mutateAsync();
					} else {
						// Determine redirect path: use current path or existing redir param
						let redirectTo: string | undefined;
						if (url.pathname !== "/" && !url.pathname.startsWith("/login")) {
							redirectTo = url.pathname + url.search + url.hash;
						} else {
							const existingRedir = url.searchParams.get("redir");
							if (existingRedir) {
								redirectTo = decodeURIComponent(existingRedir);
							}
						}

						await router.replace(
							buildLoginUrl({
								venueId,
								redirectTo,
							}),
						);
						setIsReady(true);
						return;
					}
				} else {
					if (cachedRefreshToken) {
						await loginSilent.mutateAsync();
					} else {
						if (roleId) {
							await loginSilentOffline.mutateAsync();
						} else {
							// Determine redirect path: use current path or existing redir param
							let redirectTo: string | undefined;
							if (url.pathname !== "/" && !url.pathname.startsWith("/login")) {
								redirectTo = url.pathname + url.search + url.hash;
							} else {
								const existingRedir = url.searchParams.get("redir");
								if (existingRedir) {
									redirectTo = decodeURIComponent(existingRedir);
								}
							}

							await router.replace(
								buildLoginUrl({
									venueId,
									redirectTo,
								}),
							);
							setIsReady(true);
							return;
						}
					}
				}

				const remainOfflineMutations =
					(await localforage.getItem<OfflineMutation[]>(
						STORAGE_KEYS.OFFLINE_MUTATIONS,
					)) ?? [];

				/**
				 * if user was on offline mode, cachedToken maybe null and only roleId exist
				 *  */
				const shouldGoToOfflineMode =
					isOffline ||
					(!cachedRefreshToken && !!roleId) ||
					remainOfflineMutations.length > 0;

				if (!isOffline && remainOfflineMutations.length > 0) {
					toast.message(
						`You have ${remainOfflineMutations.length} offline actions. Sync them first before using online mode.`,
						{ persist: true },
					);
				}

				// Extract and validate redirect parameter
				const redirectParam = url.searchParams.get("redir");
				const validRedirectPath = decodeRedirectPath(
					redirectParam ?? undefined,
				);

				let destinationRoute: string;
				const currentTillDestination = getCurrentTillDestination({
					pathname: router.pathname,
					asPath: router.asPath,
					isOfflineMode: shouldGoToOfflineMode,
				});

				if (validRedirectPath) {
					// User came from specific page, redirect back there
					destinationRoute = validRedirectPath;
				} else if (shouldGoToOfflineMode) {
					destinationRoute = currentTillDestination
						? currentTillDestination
						: router.pathname === "/" || router.asPath.startsWith("/login")
							? getDefaultPostLoginDestination({ isOfflineMode: true })
							: router.asPath;
				} else {
					destinationRoute = currentTillDestination
						? currentTillDestination
						: router.pathname === "/" || router.asPath.startsWith("/login")
							? getDefaultPostLoginDestination({ isOfflineMode: false })
							: router.asPath;
				}
				await router.replace(destinationRoute);
				setIsReady(true);
			} catch (e) {
				console.error(e);
				await localforage.removeItem(STORAGE_KEYS.TOKEN);
				await localforage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
				await localforage.removeItem(STORAGE_KEYS.LAST_LOGGED_IN_ROLE_ID);
				await localforage.removeItem(STORAGE_KEYS.PREFERRED_LOCATION_ON_TILL);

				// Determine redirect path: use current path or existing redir param
				let redirectTo: string | undefined;
				if (url.pathname !== "/" && !url.pathname.startsWith("/login")) {
					redirectTo = url.pathname + url.search + url.hash;
				} else {
					const existingRedir = url.searchParams.get("redir");
					if (existingRedir) {
						redirectTo = decodeURIComponent(existingRedir);
					}
				}

				await router.replace(
					buildLoginUrl({
						venueId,
						redirectTo,
					}),
				);
				setIsReady(true);
			}
		}

		void start();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [venueId, isRestoringPersistedData]);
};

const useRestoreNonSensitiveLocalStorageData = () => {
	useEffect(() => {
		async function start() {
			const states: Partial<State> = {};

			const [
				denominator,
				preferredLocation,
				preferredLocationView,
				preferredLocationOnTill,
				calendarView,
				lastOfflineDataDownloadAt,
			] = await Promise.all([
				localforage.getItem<string>(STORAGE_KEYS.DENOMINATOR),
				localforage.getItem<string>(STORAGE_KEYS.PREFERRED_LOCATION),
				localforage.getItem<string>(STORAGE_KEYS.PREFERRED_LOCATION_VIEW),
				localforage.getItem<string>(STORAGE_KEYS.PREFERRED_LOCATION_ON_TILL),
				localforage.getItem<string>(STORAGE_KEYS.PREFERRED_CALENDAR_VIEW),
				localforage.getItem<string>(STORAGE_KEYS.LAST_OFFLINE_DATA_DOWNLOAD_AT),
			]);
			if (denominator !== null) states.denominator = Number(denominator);
			if (preferredLocation !== null)
				states.preferredLocation = preferredLocation;
			if (preferredLocationView !== null)
				states.preferredLocationView =
					preferredLocationView as PREFERRED_LOCATION_VIEW;
			if (preferredLocationOnTill !== null)
				states.preferredLocationOnTill = preferredLocationOnTill;
			if (calendarView !== null)
				states.preferredCalendarView = calendarView as CALENDAR_VIEW;
			if (lastOfflineDataDownloadAt !== null) {
				states.lastOfflineDataDownloadAt = lastOfflineDataDownloadAt;
			}

			setState(states);
		}

		void start();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};
